import React, { useState, useLayoutEffect } from 'react';
import Modal from '../../common/Modal';
import {
  templateOptions,
  nonValueTemplateOptions,
} from '../constants/mailerConstants';
import { isOptionType } from '../../fields/constants/subtypes';
import CollectionChooser from './CollectionChooser';

const MailerFieldChooserModal = ({
  insertAttr,
  defaultForm,
  chooserState,
  setChooserState,
  isInjecting,
  setIsInjecting,
  insertChildAttr,
}) => {
  const { selectedForm } = chooserState;
  const { form, fieldsets, metadata_entries } = selectedForm;
  const [category, setCategory] = useState(chooserState.category);
  const [fieldsetState, setFieldsetState] = useState(
    chooserState.fieldsetState
  );
  const [field, setField] = useState(chooserState.field);
  const [attributes, setAttributes] = useState(chooserState.attributes);
  const [displayNames, setDisplayNames] = useState(chooserState.displayNames);
  const insertingRelatedForm = form.id != defaultForm.form.id;
  const canInsertCollection = insertingRelatedForm && form.submission_validate;
  const [showCollectionChooser, setShowCollectionChooser] =
    useState(canInsertCollection);
  const templateOpts = canInsertCollection
    ? nonValueTemplateOptions
    : templateOptions;

  const toggleShowChooser = () =>
    setShowCollectionChooser(!showCollectionChooser);

  function returnAttributes(model) {
    if (canInsertCollection) {
      let attrs = [
        ...nonValueTemplateOptions.find((opt) => opt.name == model).attributes,
      ];
      if (model == 'form') {
        form.form_meta_entries.forEach((fme) => {
          attrs.push('Meta: ' + fme.title);
        });
      }
      return attrs;
    }
    let attr = [...templateOptions.find((opt) => opt.name == model).attributes];
    if (model == 'submission' && form.course_validate) {
      attr = attr.concat(['course_code']);
    }
    if (model == 'fields' && isOptionType(field.type)) {
      attr.push('selected_option_id');

      let entries = metadata_entries.filter((me) => me.field_id == field.id);
      entries.forEach((me) => {
        attr.push('Meta: ' + me.title);
      });
    }
    if (model == 'form') {
      form.form_meta_entries.forEach((fme) => {
        attr.push('Meta: ' + fme.title);
      });
    }
    return attr;
  }

  function resetState(stateProp) {
    if (stateProp == 'category' || stateProp == 'fieldsetState') {
      stateProp == 'category' ? setCategory('') : null;
      setFieldsetState({ fieldset: {}, fields: [] });
    }
    setDisplayNames({ fieldsets: false, fields: false });
    setField({});
    setAttributes([]);
  }

  function grabID(model) {
    return model == 'fields'
      ? field.id
      : model == 'fieldsets'
        ? fieldsetState.fieldset.id
        : null;
  }

  const handleAttributeInsert = (attr) => {
    if (insertingRelatedForm) {
      insertAttr(category, attr, grabID(category), selectedForm.form.id);
    } else {
      insertAttr(category, attr, grabID(category));
    }
  };

  const handleChooserUnmount = () => {
    setChooserState({
      selectedForm,
      category,
      fieldsetState,
      field,
      attributes,
      displayNames,
    });
    setIsInjecting(false);
  };

  useLayoutEffect(() => {
    if (category == 'submission' || category == 'form') {
      setAttributes(returnAttributes(category));
    } else if (category == 'fieldsets') {
      if (!fieldsetState.fieldset.name) {
        setDisplayNames({ fieldsets: true, fields: false });
      } else {
        setDisplayNames({ fieldsets: false, fields: false });
        setAttributes(returnAttributes('fieldsets'));
      }
    } else if (category == 'fields') {
      if (!fieldsetState.fieldset.name) {
        setDisplayNames({ fieldsets: true, fields: false });
      } else {
        if (!field.name) {
          setDisplayNames({ fieldsets: false, fields: true });
        } else {
          setDisplayNames({ fieldsets: false, fields: false });
          setAttributes(returnAttributes('fields'));
        }
      }
    }
  }, [category, fieldsetState, field]);

  useLayoutEffect(() => {
    // reset to category if changing selectedForm
    resetState('category');
    setShowCollectionChooser(canInsertCollection);
  }, [form.id]);

  return (
    <Modal
      title='mailer-field-chooser'
      show={isInjecting}
      onClose={() => handleChooserUnmount()}
    >
      <header>
        <h3>Pick a field to add to mailer</h3>
      </header>

      {showCollectionChooser ? (
        <CollectionChooser
          chooserState={chooserState}
          insertChildAttr={insertChildAttr}
          toggleShowChooser={toggleShowChooser}
        />
      ) : (
        <div className='mailer-builder'>
          {canInsertCollection && (
            <button onClick={toggleShowChooser}>Insert Collection</button>
          )}
          <h4>Form: {form.name}</h4>

          {category ? (
            <>
              <div className='crumb'>
                <a onClick={() => resetState('category')}>{category} &raquo;</a>
              </div>

              {fieldsetState.fieldset.name && (
                <div className='crumb'>
                  <a onClick={() => resetState('fieldsetState')}>
                    {fieldsetState.fieldset.name} &raquo;
                  </a>
                </div>
              )}

              {field.name && (
                <div className='crumb'>
                  <a onClick={() => resetState('field')}>
                    {field.name} &raquo;
                  </a>
                </div>
              )}

              {displayNames.fieldsets && (
                <ul>
                  {fieldsets.map((fs) => (
                    <li key={fs.fieldset.id}>
                      <a onClick={() => setFieldsetState(fs)}>
                        {fs.fieldset.name}
                      </a>
                    </li>
                  ))}
                </ul>
              )}

              {displayNames.fields && (
                <ul>
                  {fieldsetState.fields.map((f) => (
                    <li key={f.id}>
                      <a onClick={() => setField(f)}>{f.name}</a>
                    </li>
                  ))}
                </ul>
              )}

              {attributes && (
                <ul>
                  {attributes.map((attr, i) => (
                    <li key={i}>
                      <a onClick={() => handleAttributeInsert(attr)}>{attr}</a>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <ul>
              {templateOpts.map((c) => (
                <li className='category' key={c.name}>
                  <a onClick={() => setCategory(c.name)}>{c.name}</a>
                </li>
              ))}

              {!insertingRelatedForm && (
                <li className='custom-field-link'>
                  <a onClick={() => insertAttr('custom')}>Add Custom Field</a>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
};

export default MailerFieldChooserModal;
